import { useEffect, useRef, useState } from 'react'
import {
  type FollowEnd,
  gapToSightline,
  isDrag,
  movedByHand,
  SETTLE_MS,
  SIGHTLINE,
  stepToward,
  whyStopped,
} from './follow'
import type { LiveReader } from './useLiveBook'

/** The block a reader is at, found the way everything else in the reader is. */
const blockAt = (id: string) =>
  document.querySelector<HTMLElement>(`[data-block="${CSS.escape(id)}"]`)

/**
 * Where this reader is in the book, for publishing.
 *
 * **The last block they have PASSED**, which is what `useHere` already means by
 * "where I am" one level up: a band across the window answers only while
 * something happens to be crossing it, so a short passage stops counting the
 * moment it scrolls by. The last one passed holds until they pass another.
 *
 * Read on a SETTLE rather than on a frame, and that is the whole difference
 * from the contents rail. The rail redraws itself and wants to keep up with the
 * scroll; this writes a row somebody else reads, and a reader moving
 * continuously should produce a handful of positions rather than a stream of
 * them. It also makes the scan affordable — once a settle, not sixty times a
 * second down a long book.
 */
export function useWhereIAm(blocks: number): string | null {
  const [at, setAt] = useState<string | null>(null)

  useEffect(() => {
    if (blocks === 0) return
    let timer: ReturnType<typeof setTimeout> | null = null
    let dropped = false

    const read = () => {
      timer = null
      if (dropped) return
      /*
       * The SAME line a follower rests somebody on, not the top of the reading.
       *
       * These are two halves of one fact and must agree. Publishing the last
       * block above the bar while resting a followed block a third down the
       * window means a follower reports a passage EARLIER than the one they are
       * looking at — so a chain drifts backwards a screenful at every hop, and
       * the one-hop rule the whole design rests on is quietly false.
       */
      const line = window.innerHeight * SIGHTLINE
      /* Document order is reading order, so the last block still above the line
         is the one being read — and the scan stops at the first below it. */
      let reached: string | null = null
      for (const el of document.querySelectorAll<HTMLElement>('[data-block]')) {
        if (el.getBoundingClientRect().top - line > 1) break
        reached = el.dataset.block ?? reached
      }
      setAt(reached)
    }

    const settle = () => {
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(read, SETTLE_MS)
    }

    read()
    window.addEventListener('scroll', settle, { passive: true })
    window.addEventListener('resize', settle)
    return () => {
      dropped = true
      if (timer !== null) clearTimeout(timer)
      window.removeEventListener('scroll', settle)
      window.removeEventListener('resize', settle)
    }
    /*
     * Keyed on how many blocks the book has, so the first read happens once
     * there is something to read. Mounted before the book arrives there are no
     * `[data-block]` elements to scan, and a reader who then never scrolls
     * would never publish a position at all — invisible to anybody following
     * them, and silently so, because a null position is also the ordinary state
     * of somebody who has just arrived.
     */
  }, [blocks])

  return at
}

/**
 * Keep this reader's page on somebody else's passage.
 *
 * **A per-frame pursuit rather than a scroll animation**, which is what the
 * leader moving in steps demands: `glide` starts a smooth scroll it cannot
 * cancel and cannot report the end of, so two arrivals in quick succession
 * interleave and the page is retargeted mid-flight. Closing a fraction of the
 * remaining distance each frame has nothing to cancel — a target that moves is
 * simply chased.
 *
 * **Leaving is scrolling.** Anything that moves the page other than this ends
 * it, or the first correction becomes a fight with the thing meant to be
 * helping. Our own movement is told apart by where the page was LEFT rather
 * than by a flag, because a scroll event arrives after the fact and says
 * nothing about who caused it.
 *
 * The block is looked up every frame rather than held, so a re-render, a text
 * size change or a rewrap cannot leave a stale node.
 */
export function useFollowing({
  active,
  who,
  onEnd,
}: {
  /**
   * Whether this reader is following anybody at all.
   *
   * Separate from `who` being absent, and the distinction is the whole of it:
   * nobody chosen is the ordinary state of the page, while somebody chosen who
   * is no longer in the room is the thing a reader has to be told about. Folded
   * together, every reader who is following nobody would be told continuously
   * that the person they are not following has left.
   */
  active: boolean
  /** The reader being followed, as the room last reported them. */
  who: LiveReader | undefined
  /** Why it ended, so the page can say so rather than going quiet. */
  onEnd: (why: FollowEnd) => void
}) {
  const at = active ? (who?.at ?? null) : null
  const expected = useRef(0)
  const ending = useRef(onEnd)
  ending.current = onEnd

  /*
   * Whether the reader is still there and their passage is still here, checked
   * where the answer arrives rather than inside the frame loop — a leader who
   * left is a fact about the room, and re-deciding it sixty times a second
   * would report it sixty times.
   */
  useEffect(() => {
    if (!active) return
    const why = whyStopped(who, at !== null && blockAt(at) !== null)
    if (why) ending.current(why)
  }, [active, who, at])

  useEffect(() => {
    if (!active || !at) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    let frame: number | null = null

    const step = () => {
      const node = blockAt(at)
      if (node) {
        const gap = gapToSightline(
          node.getBoundingClientRect().top,
          window.innerHeight,
        )
        const by = stepToward(gap, reduced.matches)
        if (by !== 0) {
          window.scrollBy(0, by)
          /* Where we left it, read back immediately — `scrollBy` has already
             moved the page by the time this runs, so the scroll event that
             follows finds no discrepancy. */
          expected.current = window.scrollY
        }
      }
      frame = requestAnimationFrame(step)
    }

    expected.current = window.scrollY
    frame = requestAnimationFrame(step)

    const onScroll = () => {
      if (movedByHand(window.scrollY, expected.current))
        ending.current('byHand')
    }
    const onWheel = () => ending.current('byHand')

    let from: number | null = null
    const onTouchStart = (e: TouchEvent) => {
      from = e.touches[0]?.clientY ?? null
    }
    const onTouchMove = (e: TouchEvent) => {
      if (isDrag(from, e.touches[0]?.clientY ?? null)) ending.current('byHand')
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('wheel', onWheel, { passive: true })
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: true })

    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [active, at])
}
